"use client";
import { id } from "@instantdb/react";
import { isEmpty } from "underscore";
import { InstantDb } from "../zustand/instant.schema";
import { chatState } from "../zustand";
import { userStore } from "../zustand";
import { useEffect } from "react";
import { DateTime } from "luxon";

// Custom hook to get unread count for a given roomId
export function useUnreadCountForRoom(targetRoomId, type = 'project') {
    const user = userStore((state) => state.user);
    const { data: unreadData, error } = InstantDb.useQuery({
        messages: {
            $: {
                where: {
                    // roomId: targetRoomId,
                    isRead: false,
                    'room.type': type,
                    'room.entityId': `${targetRoomId}`,
                    sender: { $not: `${user?.id}` },
                },
            },
        }
    });

    useEffect(() => {
        if (error) {
            console.error("Error in useUnreadCountForRoom: ", error);
        }
    }, [error]);
    return unreadData?.messages?.length || 0;
}

export function useChatManager() {
    const { roomId } = chatState();
    const user = userStore((state) => state.user);

    const { isLoading, error, data } = InstantDb.useQuery({
        messages: {
            $: {
                where: {
                    roomId: roomId,

                },
            }
        },
    });

    const { data: projectUnread } = InstantDb.useQuery({
        messages: {
            $: {
                where: {
                    roomId: roomId,
                    isRead: false,
                    sender: { $not: `${user?.id}` },
                    'room.type': 'project',
                },
            },
        }
    });
    const { data: unread } = InstantDb.useQuery({
        messages: {
            $: {
                where: {
                    roomId: roomId,
                    isRead: false,
                    sender: { $not: `${user?.id}` },
                    'room.type': 'user',
                },
            },
        }
    });

    useEffect(() => {
        if (error) {
            console.error("Error in chat manager: ", error);
        }
    }, [error]);

    const createMessage = async (message, fileMode = false) => {
        if (isEmpty(message.messageType) || isEmpty(message.roomId)) {
            throw new Error("Invalid message");
        }

        if (!fileMode && isEmpty(message.content)) {
            throw new Error("Invalid message");
        }

        const msgId = id()
        const updateRoomData = {
            lastActive: DateTime.now().toMillis(),
        }
        const res = await InstantDb.transact([
            InstantDb.tx.messages[msgId].update({
                content: message.content,
                messageType: message.messageType,
                roomId: message.roomId,
                isDelivered: true,
                isDeleted: false,
                isRead: false,
                isEdited: false,
                sender: `${message.sender}`,
                senderType: message.senderType,
                meta: message?.meta ?? null,
                isSaved: true,
                createdAt: JSON.stringify(new Date()),
                updatedAt: JSON.stringify(new Date()),
            }),
            InstantDb.tx.rooms[message.roomId].update(updateRoomData).link({
                msgs: msgId
            })
        ]
        );

        return res;
    };

    const updateMessage = (message) => {
        InstantDb.transact(InstantDb.tx.messages[message.id].update(message));
    };

    const deleteMessage = (message) => {
        InstantDb.transact(InstantDb.tx.messages[message.id].delete());
    };

    if (isEmpty(roomId)) {
        console.warn("roomId is empty. Skipping query execution.");
        return {
            isLoading: false,
            error: null,
            messages: [],
            createMessage: () => {
                throw new Error("Cannot create message: roomId is empty.");
            },
            deleteMessage: () => {
                throw new Error("Cannot delete message: roomId is empty.");
            },
            updateMessage: () => {
                throw new Error("Cannot update message: roomId is empty.");
            },
            getUnreadCountForRoom: () => 0,
        };
    }

    return {
        isLoading,
        error,
        messages: data?.messages,
        createMessage,
        deleteMessage,
        updateMessage,
        unread,
        projectUnread
    };
}